import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import {
  ClientColorSettingsUpdateRequest,
  ClientSettings,
  ClientSettingsUpdateResult,
} from '../models/client-settings.model';
import { ClientSettingsApiService } from './api/client-settings-api.service';

/**
 * Serviço responsável pelo gerenciamento de estado e
 * pelas regras das configurações visuais do cliente.
 *
 * As requisições HTTP são delegadas ao
 * ClientSettingsApiService.
 */
@Injectable({
  providedIn: 'root',
})
export class ClientSettingsService {
  private readonly clientSettingsApiService = inject(ClientSettingsApiService);

  private readonly transloco = inject(TranslocoService);

  // ─── Estado ────────────────────────────────────────────────────────────────

  private readonly _settings = signal<ClientSettings | null>(null);

  private readonly _isLoading = signal(false);

  /**
   * Configurações visuais atualmente carregadas.
   */
  readonly settings = this._settings.asReadonly();

  /**
   * Indica se alguma operação está sendo processada.
   */
  readonly isLoading = this._isLoading.asReadonly();

  // ─── Consultas ─────────────────────────────────────────────────────────────

  /**
   * Busca as configurações visuais do cliente
   * associado ao usuário autenticado.
   *
   * GET /api/client-theme
   */
  async getCurrent(): Promise<ClientSettings | null> {
    this._isLoading.set(true);

    try {
      const settings = await this.clientSettingsApiService.getCurrent();

      this._settings.set(settings);

      return settings;
    } catch (error: unknown) {
      console.error('Erro ao buscar as configurações visuais do cliente atual:', error);

      this._settings.set(null);

      return null;
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Busca as configurações visuais de um cliente
   * pelo identificador.
   *
   * GET /api/client-theme/{clientId}
   */
  async getByClientId(clientId: string): Promise<ClientSettings | null> {
    this._isLoading.set(true);

    try {
      const normalizedClientId = this.normalizeRequiredValue(clientId, 'shared.clientSettings.errors.clientIdRequired');

      const settings = await this.clientSettingsApiService.getByClientId(normalizedClientId);

      this._settings.set(settings);

      return settings;
    } catch (error: unknown) {
      console.error('Erro ao buscar as configurações visuais do cliente:', error);

      this._settings.set(null);

      return null;
    } finally {
      this._isLoading.set(false);
    }
  }

  // ─── Tema e cores ──────────────────────────────────────────────────────────

  /**
   * Atualiza o tema e as cores de um cliente.
   *
   * PUT /api/client-theme/{clientId}/colors
   */
  async updateClientColorSettings(
    clientId: string,
    data: ClientColorSettingsUpdateRequest,
  ): Promise<ClientSettingsUpdateResult> {
    this._isLoading.set(true);

    try {
      const normalizedClientId = this.normalizeRequiredValue(clientId, 'shared.clientSettings.errors.clientIdRequired');

      const sanitizedData = this.sanitizeColorSettings(data);

      const updatedSettings = await this.clientSettingsApiService.updateColors(normalizedClientId, sanitizedData);

      this._settings.set(updatedSettings);

      return {
        success: true,
        data: updatedSettings,
      };
    } catch (error: unknown) {
      console.error('Erro ao atualizar o tema e as cores do cliente:', error);

      return {
        success: false,
        error: this.getErrorMessage(error),
      };
    } finally {
      this._isLoading.set(false);
    }
  }

  // ─── Logo ──────────────────────────────────────────────────────────────────

  /**
   * Envia ou substitui o logotipo do cliente.
   *
   * PUT /api/client-theme/{clientId}/logo
   *
   * O arquivo é enviado como multipart/form-data
   * pelo ClientSettingsApiService.
   */
  async updateClientLogo(clientId: string, file: File): Promise<ClientSettingsUpdateResult> {
    this._isLoading.set(true);

    try {
      const normalizedClientId = this.normalizeRequiredValue(clientId, 'shared.clientSettings.errors.clientIdRequired');

      this.validateLogoFile(file);

      const updatedSettings = await this.clientSettingsApiService.updateLogo(normalizedClientId, file);

      this._settings.set(updatedSettings);

      return {
        success: true,
        data: updatedSettings,
      };
    } catch (error: unknown) {
      console.error('Erro ao atualizar o logotipo do cliente:', error);

      return {
        success: false,
        error: this.getErrorMessage(error),
      };
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Remove o logotipo atual do cliente.
   *
   * DELETE /api/client-theme/{clientId}/logo
   */
  async removeClientLogo(clientId: string): Promise<ClientSettingsUpdateResult> {
    this._isLoading.set(true);

    try {
      const normalizedClientId = this.normalizeRequiredValue(clientId, 'shared.clientSettings.errors.clientIdRequired');

      const updatedSettings = await this.clientSettingsApiService.removeLogo(normalizedClientId);

      this._settings.set(updatedSettings);

      return {
        success: true,
        data: updatedSettings,
      };
    } catch (error: unknown) {
      console.error('Erro ao remover o logotipo do cliente:', error);

      return {
        success: false,
        error: this.getErrorMessage(error),
      };
    } finally {
      this._isLoading.set(false);
    }
  }

  // ─── Restauração ───────────────────────────────────────────────────────────

  /**
   * Restaura as configurações visuais do cliente
   * para os valores padrão.
   *
   * PATCH /api/client-theme/{clientId}/reset
   */
  async resetClientSettings(clientId: string): Promise<ClientSettingsUpdateResult> {
    this._isLoading.set(true);

    try {
      const normalizedClientId = this.normalizeRequiredValue(clientId, 'shared.clientSettings.errors.clientIdRequired');

      const resetSettings = await this.clientSettingsApiService.reset(normalizedClientId);

      this._settings.set(resetSettings);

      return {
        success: true,
        data: resetSettings,
      };
    } catch (error: unknown) {
      console.error('Erro ao restaurar as configurações visuais:', error);

      return {
        success: false,
        error: this.getErrorMessage(error),
      };
    } finally {
      this._isLoading.set(false);
    }
  }

  // ─── Estado local ──────────────────────────────────────────────────────────

  /**
   * Atualiza manualmente as configurações no estado local.
   */
  setSettings(settings: ClientSettings | null): void {
    this._settings.set(settings);
  }

  /**
   * Limpa as configurações armazenadas no estado local.
   */
  clearSettings(): void {
    this._settings.set(null);
  }

  // ─── Normalização e validação ──────────────────────────────────────────────

  /**
   * Normaliza os valores de cores antes do envio.
   *
   * Strings vazias são convertidas em null.
   * Cores válidas são padronizadas em letras maiúsculas.
   */
  private sanitizeColorSettings(data: ClientColorSettingsUpdateRequest): ClientColorSettingsUpdateRequest {
    if (!data) {
      throw new Error(this.transloco.translate('shared.clientSettings.errors.colorSettingsRequired'));
    }

    return {
      defaultTheme: data.defaultTheme,

      primaryColor: this.normalizeColor(data.primaryColor),

      secondaryColor: this.normalizeColor(data.secondaryColor),

      accentColor: this.normalizeColor(data.accentColor),
    };
  }

  /**
   * Normaliza uma cor hexadecimal opcional.
   *
   * A validação definitiva também deve existir
   * no backend.
   */
  private normalizeColor(value: string | null | undefined): string | null {
    const normalizedValue = value?.trim().toUpperCase();

    if (!normalizedValue) {
      return null;
    }

    if (!/^#[0-9A-F]{6}$/.test(normalizedValue)) {
      throw new Error(this.transloco.translate('shared.clientSettings.errors.invalidColorFormat', { value }));
    }

    return normalizedValue;
  }

  /**
   * Valida o arquivo antes do upload.
   */
  private validateLogoFile(file: File | null | undefined): void {
    if (!file) {
      throw new Error(this.transloco.translate('shared.clientSettings.errors.logoRequired'));
    }

    if (file.size <= 0) {
      throw new Error(this.transloco.translate('shared.clientSettings.errors.emptyFile'));
    }

    const allowedTypes = new Set<string>(['image/png', 'image/jpeg', 'image/webp']);

    if (!allowedTypes.has(file.type)) {
      throw new Error(this.transloco.translate('shared.clientSettings.errors.invalidLogoFormat'));
    }

    const maximumFileSize = 5 * 1024 * 1024;

    if (file.size > maximumFileSize) {
      throw new Error(this.transloco.translate('shared.clientSettings.errors.maxLogoSize'));
    }
  }

  /**
   * Normaliza um valor textual obrigatório.
   */
  private normalizeRequiredValue(value: string | null | undefined, errorKey: string): string {
    const normalizedValue = value?.trim();

    if (!normalizedValue) {
      throw new Error(this.transloco.translate(errorKey));
    }

    return normalizedValue;
  }

  // ─── Tratamento de erros ───────────────────────────────────────────────────

  /**
   * Converte erros da API ou erros locais
   * em mensagens adequadas para a interface.
   */
  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return this.clientSettingsApiService.getErrorMessage(error);
    }

    if (error instanceof Error) {
      return error.message;
    }

    return this.transloco.translate('shared.clientSettings.errors.processVisualSettings');
  }
}
